import styled from 'styled-components';
import TextBox from 'components/TextBox';
import { IToken } from 'store/ducks/globalTypes';
import { Input } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { themesList } from 'constants/themesList';

const { TextArea } = Input;

export const ColumnHeaderContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
`;

export const SearchContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-left: 50px;
`;

export const SearchInput = styled(Input)`
  width: 200px;
  height: 24px;
  margin-left: 10px;

  .ant-input {
    font-size: 12px;
    padding: 2px 6px;
  }
`;

export const SearchIcon = styled(SearchOutlined)<{ scheme?: string }>`
  font-size: 18px;
  cursor: pointer;
  margin-left: 70px;
`;

export const MergedColumnsContainer = styled('div')`
  display: flex;
`;

export const MergedColumnSm = styled('div')<IToken & { scheme?: string }>`
  width: 200px;
  position: relative;

  &:before {
    position: absolute;
    top: 50%;
    inset-inline-end: 0;
    width: 1px;
    height: 1.6em;
    background-color: ${(props) => {
      const currentTheme =
        themesList[
          (props.scheme || 'deepBlueTheme') as keyof typeof themesList
        ];
      return currentTheme?.tableBorder || props.token.colorBgMask;
    }};
    transform: translateY(-50%);
    transition: background-color 0.2s;
    content: '';
    display: block;
  }
`;

export const MergedColumnMd = styled('div')<IToken>`
  width: 450px;
  padding-left: ${(props) => `${props.token.padding}px`};
`;

export const StyledTextBox = styled(TextBox)`
  padding: 0 !important;
`;

export const LeftAlignedContent = styled('div')`
  margin-left: -55px;
  padding-left: 5px;
`;

export const DroppedAuditFindingsContent = styled('div')`
  margin-left: -20px !important;
  padding-left: 5px;
  transform: translateX(-40px);
  width: 390px;

  > div > div {
    width: 380px !important;
    margin-left: -20px !important;

    /* Force black text for deep navy theme */
    color: #000000 !important;
  }

  /* Additional specificity for deep navy theme */
  .deepNavyTheme & > div > div,
  .deepNavyTheme & div {
    color: #000000 !important;
  }
`;

export const GapsNoticedContent = styled('div')`
  margin-left: -30px !important;
  padding-left: 5px;
  transform: translateX(-10px);

  /* Target only the checkbox container */
  .ant-checkbox-wrapper {
    margin-left: -20px !important;
  }
`;

export const CategoryContent = styled('div')`
  margin-left: -50px !important;
  padding-left: 5px;
  position: relative;
  z-index: 1000;
  transform: translateX(-30px);
`;

export const PlaceholderDiv = styled('div')<{ width: number; height: number }>`
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
`;

export const ClientReqTextArea = styled(TextArea)<{
  level?: number;
  width?: number;
  scheme?: string;
}>`
  width: ${({ width = 445 }) => width}px;
  padding-left: 5px;
  resize: both !important;

  ${(props) =>
    props.scheme === 'darkUITheme' ||
    props.scheme === 'bluishTheme' ||
    props.scheme === 'deepBlueTheme' ||
    props.scheme === 'deepNavyTheme'
      ? 'color: #000000 !important;'
      : ''}

  /* Base sizes */
&::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: linear-gradient(135deg, #f3f3f3, #dadada);
    border-radius: 10px;
    box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.15);
  }

  /* Thumb */
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #8aa0b8, #6e8299);
    border-radius: 10px;
    border: 2px solid #f3f3f3; /* gives a "pill" shape cutout */
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }

  /* Hover state */
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #7e95af, #5e738c);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
  }

  /* Active state */
  &::-webkit-scrollbar-thumb:active {
    background: linear-gradient(135deg, #6c84a0, #4f637a);
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #6e8299 #dadada;
`;

export const CategoryHeaderContainer = styled('div')<{ scheme?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  position: relative;
  z-index: 1001;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  background-color: transparent;
  min-width: 100px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  span {
    font-size: 13px;
    white-space: nowrap;
  }
`;

export const CategoryFilterIcon = styled(FilterOutlined)<{ scheme?: string }>`
  font-size: 12px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#ffffff' : '#666666')};
  flex-shrink: 0;
  cursor: pointer;
`;

export const CategoryDropdownPortal = styled('div')`
  position: absolute;
  top: calc(100% + 8px);
  left: -50px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  z-index: 99999;
  padding: 4px;
  min-width: 180px;
  max-height: 250px;
  overflow-y: auto;
`;

export const DropdownOption = styled('div')<{ selected?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  background-color: ${(props) => (props.selected ? '#e6f7ff' : 'transparent')};
  color: #000000;
  border-bottom: 1px solid #f0f0f0;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => (props.selected ? '#e6f7ff' : '#f5f5f5')};
  }
`;

export const HiddenMainRowStyles = `
  .hidden-placeholder-row {
    height: 0 !important;
    overflow: hidden !important;
  }
  .hidden-placeholder-row td {
    height: 0 !important;
    padding: 0 !important;
    border: none !important;
    overflow: hidden !important;
  }
  .hidden-placeholder-row td > * {
    visibility: hidden !important;
    height: 0 !important;
  }
`;
